import React from "react";
import { motion } from "framer-motion";
import { FaTrophy, FaMedal, FaHeart } from "react-icons/fa";
import founderImage from "../images/landingPage/karunakar.webp";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.16 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const highlights = [
  { icon: FaMedal, label: "10+ years teaching" },
  { icon: FaHeart, label: "1000+ students" },
  { icon: FaTrophy, label: "Record-holder" },
];

export default function AboutFounder() {
  return (
    <section className="bg-gray-50 px-4 py-24 sm:py-28 md:px-12">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-14 md:flex-row md:gap-20">
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex w-full justify-center md:w-[42%]"
        >
          <div className="relative">
            {/* soft neutral frame + single subtle brand accent corner */}
            <div className="absolute -left-4 -top-4 h-24 w-24 rounded-tl-3xl border-l-2 border-t-2 border-brand-300" />
            <img
              src={founderImage}
              alt="Karunakar Gorentla, Founder of ABC School of Music"
              className="relative h-[26rem] w-72 rounded-2xl object-cover object-top shadow-card md:h-[32rem] md:w-96"
            />
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="absolute -bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-ink shadow-card ring-1 ring-gray-100"
            >
              <FaTrophy className="text-brand" /> Founder &amp; Head Educator
            </motion.div>
          </div>
        </motion.div>

        {/* Text */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="w-full space-y-5 md:w-[58%]"
        >
          <motion.span variants={itemVariants} className="eyebrow">
            About the Founder
          </motion.span>

          <motion.h2
            variants={itemVariants}
            className="font-display text-3xl font-bold text-ink md:text-5xl"
          >
            Meet <span className="text-brand">Karunakar Gorentla</span>
          </motion.h2>

          <motion.p
            variants={itemVariants}
            className="text-base leading-relaxed text-ink-soft md:text-lg"
          >
            Karunakar is a passionate music educator who has helped{" "}
            <span className="font-semibold text-ink">
              thousands of students
            </span>{" "}
            unlock their musical potential.
          </motion.p>

          <motion.p
            variants={itemVariants}
            className="text-base leading-relaxed text-ink-soft md:text-lg"
          >
            With over{" "}
            <span className="font-semibold text-ink">10 years of experience</span>
            , he blends classical and contemporary styles into a hands-on,
            engaging teaching method.
          </motion.p>

          <motion.p
            variants={itemVariants}
            className="text-base leading-relaxed text-ink-soft md:text-lg"
          >
            Recognized by the{" "}
            <span className="font-semibold text-ink">
              World, Asian, and Indian Book of Records
            </span>
            , he continues to inspire students to dream big and love the art of
            music.
          </motion.p>

          <motion.div variants={itemVariants} className="flex flex-wrap gap-3 pt-2">
            {highlights.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm text-ink-soft"
              >
                <Icon className="text-brand" /> {label}
              </span>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
